import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  Tabs,
  Tag,
  Button,
  List,
  Empty,
  ConfigProvider,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrders } from '../store/slices/ordersSlice';
import { formatPrice, formatDateTime, getOrderStatusColor, getOrderStatusText } from '../utils/formatters';
import Loader from '../components/common/Loader';

const { Title, Text } = Typography;

const OrdersPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orders, loading, pagination } = useSelector((state) => state.orders);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const params = {
      page: 1,
      limit: 20,
      ...(statusFilter !== 'all' && { status: statusFilter }),
    };
    dispatch(fetchOrders(params));
  }, [dispatch, statusFilter]);

  const handleTabChange = (key) => {
    setStatusFilter(key);
  };

  const handleOrderClick = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  if (loading && orders.length === 0) {
    return <Loader text="Buyurtmalar yuklanmoqda..." />;
  }

  const items = [
    { label: 'Hammasi', key: 'all' },
    { label: 'Kutilmoqda', key: 'pending' },
    { label: 'Tasdiqlangan', key: 'confirmed' },
    { label: 'Jarayonda', key: 'processing' },
    { label: 'Yakunlangan', key: 'completed' },
    { label: 'Bekor qilingan', key: 'cancelled' },
  ];

  // Helper to map backend colors to Ant Design Tag colors
  const getTagColor = (status) => {
    const colorMap = {
      pending: 'orange',
      confirmed: 'blue',
      processing: 'cyan',
      completed: 'green',
      cancelled: 'red',
    };
    // If the util returns a hex code, we might need adjustments or just pass it directly if it's a valid color code for Tag
    // But assuming the util returns semantic strings or we use the status directly
    return colorMap[status] || getOrderStatusColor(status);
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 20px' }}>
      <Title level={2} style={{ marginBottom: 24, color: '#f8fafc', fontWeight: 800, fontFamily: 'Outfit' }}>
        Mening buyurtmalarim
      </Title>

      <ConfigProvider theme={{ components: { Tabs: { itemColor: 'rgba(255,255,255,0.65)', itemHoverColor: '#ffffff', itemSelectedColor: '#38bdf8' } } }}>
        <Tabs
          activeKey={statusFilter}
          onChange={handleTabChange}
          items={items}
          style={{ marginBottom: 24 }}
        />
      </ConfigProvider>

      {orders.length === 0 ? (
        <div style={{ padding: '64px 0', textAlign: 'center' }}>
          <Card style={{ borderRadius: 24, boxShadow: '0 12px 32px rgba(0,0,0,0.1)' }}>
            <Empty description={<span style={{ color: '#475569' }}>Buyurtmalar topilmadi</span>}>
              <Button type="primary" shape="round" size="large" onClick={() => navigate('/products')}>
                Xarid qilish
              </Button>
            </Empty>
          </Card>
        </div>
      ) : (
        <List
          grid={{ gutter: 16, column: 1 }}
          dataSource={orders}
          renderItem={(order) => (
            <List.Item>
              <Card
                hoverable
                onClick={() => handleOrderClick(order.id)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 16 }}>
                  <div>
                    <Title level={4} style={{ margin: 0 }}>
                      Buyurtma #{order.order_number}
                    </Title>
                    <Text type="secondary">
                      {formatDateTime(order.created_at)}
                    </Text>
                  </div>
                  <Tag color={getTagColor(order.status)} style={{ fontSize: 14, padding: '4px 8px' }}>
                    {getOrderStatusText(order.status)}
                  </Tag>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text type="secondary">
                    {order.orderItems?.length || 0} mahsulot
                  </Text>
                  <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                    {formatPrice(order.total_amount)}
                  </Title>
                </div>

                {order.delivery_address && (
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary">
                      Manzil: {order.delivery_address}
                    </Text>
                  </div>
                )}
              </Card>
            </List.Item>
          )}
        />
      )}

      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
          <Text style={{ color: '#94a3b8' }}>
            Sahifa {pagination.page} / {pagination.totalPages}
          </Text>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
